import { ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { ArrowDown, ArrowUp, ArrowUpDown, Edit } from 'lucide-react';
import { RouterOutput } from 'src/trpc';
import { StatusDisplay } from '../../../components/StatusDisplay';

// Define the type for organization data from the API
type Organization =
  RouterOutput['admin']['organization']['getAllOrganizations']['organizations'][0];

export const createOrganizationColumns = (
  onEdit: (organization: Organization) => void,
): ColumnDef<Organization>[] => [
  {
    accessorKey: 'name',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Name
          {!column.getIsSorted() && <ArrowUpDown className="ml-2 h-4 w-4" />}
          {column.getIsSorted() === 'asc' && <ArrowUp className="ml-2 h-4 w-4" />}
          {column.getIsSorted() === 'desc' && <ArrowDown className="ml-2 h-4 w-4" />}
        </Button>
      );
    },
  },
  {
    accessorKey: 'status',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Status
          {!column.getIsSorted() && <ArrowUpDown className="ml-2 h-4 w-4" />}
          {column.getIsSorted() === 'asc' && <ArrowUp className="ml-2 h-4 w-4" />}
          {column.getIsSorted() === 'desc' && <ArrowDown className="ml-2 h-4 w-4" />}
        </Button>
      );
    },
    cell: ({ row }) => <StatusDisplay status={row.getValue('status')} />,
  },
  {
    accessorKey: 'fromEmail',
    header: 'From Email',
  },
  {
    accessorKey: 'toEmail',
    header: 'To Email',
  },
  {
    accessorKey: 'createdAt',
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Created At
          {!column.getIsSorted() && <ArrowUpDown className="ml-2 h-4 w-4" />}
          {column.getIsSorted() === 'asc' && <ArrowUp className="ml-2 h-4 w-4" />}
          {column.getIsSorted() === 'desc' && <ArrowDown className="ml-2 h-4 w-4" />}
        </Button>
      );
    },
    cell: ({ row }) => {
      // Handle different date formats that might come from the API
      const value = row.getValue('createdAt');
      if (!value) return 'N/A';

      // If it's already a Date object
      if (value instanceof Date) {
        return value.toLocaleDateString();
      }

      // If it's a string or number, convert to Date
      try {
        return new Date(value as string | number).toLocaleDateString();
      } catch {
        return 'Invalid date';
      }
    },
  },
  {
    id: 'actions',
    header: 'Edit',
    cell: ({ row }) => {
      return (
        <Button
          size="sm"
          variant="outline"
          onClick={(e) => {
            e.stopPropagation(); // Prevent row click from triggering
            onEdit(row.original);
          }}
        >
          <Edit className="h-4 w-4" />
        </Button>
      );
    },
  },
];
